import { useEffect, useState } from "react";
import { Sun, Moon } from "lucide-react";

/**
 * Theme toggle: light / dark.
 *
 * v109.9.36 — Replaced the dropdown with a segmented two-button toggle
 * (Sun / Moon) so both options are always visible and reachable in a single
 * click, matching the same interaction pattern as the Card/List View toggle.
 *
 * Preference persists in localStorage under "pk-theme".
 */
function applyTheme(mode) {
  const root = document.documentElement;
  const isDark = mode === "dark";
  root.classList.toggle("dark", isDark);
  root.style.colorScheme = isDark ? "dark" : "light";
}

export function initTheme() {
  // Default everyone to Light. Honour an explicit "dark" preference only when
  // the user has previously chosen it via the toggle.
  const saved = localStorage.getItem("pk-theme");
  applyTheme(saved === "dark" ? "dark" : "light");
}

export function ThemeToggle() {
  const [mode, setMode] = useState(() => (localStorage.getItem("pk-theme") === "dark" ? "dark" : "light"));

  useEffect(() => {
    applyTheme(mode);
    localStorage.setItem("pk-theme", mode);
  }, [mode]);

  const isDark = mode === "dark";
  const toggle = () => setMode(isDark ? "light" : "dark");

  return (
    <button
      type="button"
      onClick={toggle}
      role="switch"
      aria-checked={isDark}
      aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
      title={isDark ? "Dark mode · click for Light" : "Light mode · click for Dark"}
      className={`relative w-10 h-10 rounded-full grid place-items-center overflow-hidden transition-colors duration-500 ${
        isDark
          ? "bg-indigo-950 hover:bg-indigo-900 text-indigo-100"
          : "bg-amber-100 hover:bg-amber-200 text-amber-600"
      }`}
      data-testid="theme-toggle"
      data-theme={mode}
    >
      {/* Sun — shown in Light mode. Rotates and fades out when Dark is engaged. */}
      <Sun
        size={18}
        strokeWidth={2.2}
        data-keep-color=""
        data-testid="theme-toggle-sun"
        className={`absolute transition-all duration-500 ease-[cubic-bezier(.65,.05,.36,1)] ${
          isDark ? "opacity-0 rotate-180 scale-50" : "opacity-100 rotate-0 scale-100"
        }`}
      />
      {/* Moon — shown in Dark mode. Rotates and fades in when Dark is engaged. */}
      <Moon
        size={18}
        strokeWidth={2.2}
        data-keep-color=""
        data-testid="theme-toggle-moon"
        className={`absolute transition-all duration-500 ease-[cubic-bezier(.65,.05,.36,1)] ${
          isDark ? "opacity-100 rotate-0 scale-100" : "opacity-0 -rotate-180 scale-50"
        }`}
      />
      {/* Hidden aliases so previous testid selectors still bind */}
      <span data-testid="theme-light" hidden />
      <span data-testid="theme-dark" hidden />
    </button>
  );
}
